import type PubNub from 'pubnub'
import AutoAwesome from '@mui/icons-material/AutoAwesome'
import Close from '@mui/icons-material/Close'
import Search from '@mui/icons-material/Search'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined'
import Lock from '@mui/icons-material/Lock'
import { Box, IconButton, InputBase, Popover, Button, Typography, Divider } from '@mui/material'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useFocusOnOpen } from '~/shared/hooks/useFocusOnOpen'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import CaseMessageSearch from './Chat/CaseMessageSearch'
import { useMessageSelection, copyMessagesToClipboard } from './Chat/selection'
import CurrentProfileViewers from './CurrentProfileViewers'
import PersonChatSummaryCard from './PersonChatSummaryCard'
import AddPhoneCallButton from './PhoneCall/PhoneCall'
import { borderSx } from '~/shared/lib/borderSx'
import { BLUE } from '~/shared/lib/theme'
import {
  bffCrmPersonChat,
  BffCrmPersonChatRetrievePathParams,
  BffCrmPersonChatRetrieveQueryParams,
  BffCrmPersonChatRetrieveResponse,
} from '@fireflyhealth/api'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { Loader } from '~/shared/ui'
import { MacrosProvider } from '~/entities/macro'
import { ChatInput } from './Chat/ChatInput'
import { Virtualizer, VirtualizerHandle } from 'virtua'
import { assertNever, assertPropertyIsNotNull } from '~/shared/lib/types'
import ChatMessage from './Chat/ChatMessage'
import EmailChatMessage from './Email/EmailChatMessage'
import PhoneCallMessage from './PhoneCall/PhoneCallMessage'
import ChatMessageDocument from './Chat/ChatMessageDocument'
import { ChatMessageAttachment } from './Chat/ChatMessageAttachment'
import { ChatMessageRelations } from './Chat/ChatMessageRelations'
import ChatTypingIndicator from './Chat/ChatTypingIndicator'
import { useMessageSearch } from './ChatServiceV2'
import { PhoneCallsKey } from './PhoneCallService'
import { ChatSearchResultsV2 } from './Chat/ChatSearchResultsV2'
import { useSendChatMutation } from './Chat/utils'
import ChatMessageEvent from './Chat/ChatMessageEvent'
import { TOOLBAR_ICON_BUTTON_SX } from './toolbarSx'

const DIVIDER_SX = { height: '50%', width: '2px', bgcolor: 'grey.300', my: '2rem' }

const ARROW_BUTTON_SX = { alignSelf: 'center', fontSize: '1.5rem' }

type At = {
  kind: Required<BffCrmPersonChatRetrieveQueryParams['kind']>
  objectId: Required<BffCrmPersonChatRetrieveQueryParams['object_id']>
}

const useChatInfiniteQuery = (
  personId: BffCrmPersonChatRetrievePathParams['person_id'],
  {
    caseId = undefined,
    at = undefined,
  }: {
    caseId: BffCrmPersonChatRetrieveQueryParams['case_id']
    at?: At
  },
) => {
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    hasPreviousPage,
    fetchPreviousPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery({
    queryKey: ['chat', personId, caseId, at?.kind, at?.objectId],
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      const { data } = await bffCrmPersonChat.retrieve(
        { person_id: personId },
        {
          params: {
            cursor: pageParam ?? undefined,
            case_id: caseId ?? undefined,
            kind: !pageParam ? (at?.kind ?? undefined) : undefined,
            object_id: !pageParam ? (at?.objectId ?? undefined) : undefined,
          },
        },
      )
      return data
    },
    initialPageParam: null,
    getNextPageParam: lastPage =>
      lastPage.communications.has_next ? lastPage.communications.next : undefined,
    getPreviousPageParam: firstPage =>
      firstPage.communications.has_previous ? firstPage.communications.previous : undefined,
  })

  return {
    data,
    // Since "next" and "previous" are reversed in the UI, renaming these here
    // to help avoid confusion.
    hasEarlierResults: hasNextPage,
    fetchEarlierResults: fetchNextPage,
    isFetchingEarlierResults: isFetchingNextPage,
    hasLaterResults: hasPreviousPage,
    fetchLaterResults: fetchPreviousPage,
    isFetchingLaterResults: isFetchingPreviousPage,
  }
}

const useChatURLState = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const params = new URLSearchParams(location.search)

  const caseIdParam = params.get('caseId')
  const caseId = caseIdParam ? Number(caseIdParam) : null

  const scrollToMessageId = params.get('scrollToMessageId')

  const at: At | null = scrollToMessageId
    ? { kind: 'chat', objectId: Number(scrollToMessageId) }
    : null

  const searchQuery = params.get('search')

  const state = {
    caseId,
    at,
    searchQuery,
  }

  const setState = useCallback(
    ({ caseId, at, searchQuery }: Partial<typeof state>) => {
      const params = new URLSearchParams(location.search)

      if (caseId === null) {
        params.delete('caseId')
      } else if (caseId !== undefined) {
        params.set('caseId', String(caseId))
      }

      if (at === null) {
        params.delete('scrollToMessageId')
      } else if (at !== undefined) {
        params.set('scrollToMessageId', String(at.objectId))
      }

      if (searchQuery === null) {
        params.delete('search')
      } else if (searchQuery !== undefined) {
        params.set('search', searchQuery)
      }

      void navigate({ pathname: location.pathname, search: params.toString() })
    },
    [location, navigate],
  )

  return [state, setState] as const
}

const useSearchState = () => {
  // UX state for search mode.
  //
  // "Search mode" starts from the moment you click the magnifying glass icon
  // and can start typing in your search query, and ends when you click the "X"
  // icon to exit search. All other times, this state will be null.
  //
  // Within search mode, you transition between:
  // - Constructing a search query
  // - Executing the query and viewing the results
  // - Selecting a result and alternating between results
  const [state, setState] = useState<{
    // Whether we're viewing the search results or a specific result.
    view: 'results' | 'single'
    // Index of the currently selected result, null if a result has not yet been
    // selected.
    resultIndex: number | null
  } | null>(null)

  // Start search mode on the results view (probably empty).
  const start = useCallback(() => {
    setState({
      view: 'results',
      resultIndex: null,
    })
  }, [setState])

  // Select an view a specific result.
  const selectResult = useCallback(
    (index: number) => {
      setState({
        view: 'single',
        resultIndex: index,
      })
    },
    [setState],
  )

  // Go back to the list of results.
  const backToResults = useCallback(() => {
    setState(previous => ({
      view: 'results',
      resultIndex: previous?.resultIndex ?? null,
    }))
  }, [setState])

  // Exit search mode.
  const end = useCallback(() => {
    setState(null)
  }, [setState])

  return {
    state,
    start,
    selectResult,
    backToResults,
    end,
  }
}

const ChatContext = createContext<
  | (({
      communications: BffCrmPersonChatRetrieveResponse['communications']['results']
      setOptimisticallySentCommunication: (
        communication: BffCrmPersonChatRetrieveResponse['communications']['results'][number] | null,
      ) => void
    } & Pick<BffCrmPersonChatRetrieveResponse, 'me' | 'person' | 'thread' | 'case'> & {
        at: ReturnType<typeof useChatURLState>[0]['at']
        findIndex: () => number | undefined
      } & Pick<
        ReturnType<typeof useChatInfiniteQuery>,
        | 'hasEarlierResults'
        | 'fetchEarlierResults'
        | 'isFetchingEarlierResults'
        | 'hasLaterResults'
        | 'fetchLaterResults'
        | 'isFetchingLaterResults'
      > & {
        goTo: (at: At | null) => void
      }) & {
      search: {
        result: ReturnType<typeof useMessageSearch>
        query: ReturnType<typeof useChatURLState>[0]['searchQuery']
        setQuery: (searchQuery: string | null) => void
        nextResult: () => void
        previousResult: () => void
      } & Pick<
        ReturnType<typeof useSearchState>,
        'state' | 'start' | 'selectResult' | 'backToResults' | 'end'
      >
    } & {
      selection: ReturnType<typeof useMessageSelection> & {
        handleCopy: () => Promise<void>
        isCopyFlash: boolean
      }
    })
  | undefined
>(undefined)

const ChatLoader: React.FC<
  React.PropsWithChildren<{
    personId: BffCrmPersonChatRetrievePathParams['person_id']
  }>
> = ({ personId, children }) => {
  const [urlState, setUrlState] = useChatURLState()
  const { caseId, at, searchQuery } = urlState

  const {
    data,
    hasEarlierResults,
    fetchEarlierResults,
    isFetchingEarlierResults,
    hasLaterResults,
    fetchLaterResults,
    isFetchingLaterResults,
  } = useChatInfiniteQuery(personId, {
    caseId: caseId ?? undefined,
    at: at ?? undefined,
  })

  // Message that user is in process of sending, to be rendered optimistically.
  const [optimisticallySentCommunication, setOptimisticallySentCommunication] = useState<
    BffCrmPersonChatRetrieveResponse['communications']['results'][number] | null
  >(null)

  const {
    communications,
    me,
    person,
    thread,
    case: case_,
  } = useMemo(() => {
    const latestPage = data?.pages.at(-1)
    if (!data || !latestPage) {
      return {}
    }

    return {
      // Reverse to match display order.
      communications: [
        ...data.pages.flatMap(page => page.communications.results).reverse(),
        ...(!hasLaterResults && optimisticallySentCommunication
          ? [optimisticallySentCommunication]
          : []),
      ],
      me: latestPage.me,
      person: latestPage.person,
      thread: latestPage.thread,
      case: latestPage.case,
    }
  }, [data, hasLaterResults, optimisticallySentCommunication])

  const findIndex = useCallback(() => {
    if (!at || !communications) {
      return
    }

    return communications.findIndex(
      ({ content_object }) => content_object.kind === at.kind && content_object.id === at.objectId,
    )
  }, [at, communications])

  const queryClient = useQueryClient()

  const reset = useCallback(() => {
    void queryClient.resetQueries({ queryKey: ['chat', personId] })
  }, [queryClient, personId])

  const goTo = useCallback(
    (at: Parameters<typeof setUrlState>[0]['at']) => {
      reset()
      setUrlState({ at })
    },
    [reset, setUrlState],
  )

  const searchState = useSearchState()

  const search = useMessageSearch(person?.user?.id)
  // `search` and `searchState` are rebuilt on every render, so the effects below depend on
  // the stable callbacks rather than the objects that hold them.
  const { search: runSearch, clear: clearSearch, data: searchData } = search
  const { state: searchStateValue, start: startSearchState, end: endSearchState } = searchState
  const { selectResult } = searchState

  // Execute search based on URL state.
  useEffect(() => {
    if (searchQuery !== null && searchQuery !== '') {
      void runSearch(searchQuery)
    } else {
      clearSearch()
    }
  }, [searchQuery, runSearch, clearSearch])

  // Update search state when URL state changes.
  useEffect(() => {
    if (searchQuery !== null) {
      startSearchState()
    } else {
      endSearchState()
    }
  }, [searchQuery, startSearchState, endSearchState])

  const startSearch = useCallback(() => {
    setUrlState({ searchQuery: '' })
  }, [setUrlState])

  const setSearchQuery = useCallback(
    (searchQuery: string | null) => {
      setUrlState({ searchQuery })
    },
    [setUrlState],
  )

  const selectSearchResult = useCallback(
    (index: number) => {
      const result = searchData?.results?.[index]
      if (!result) {
        throw new Error('Attempted to call selectSearchResult with an invalid index')
      }

      selectResult(index)
      goTo({ kind: 'chat', objectId: result.id })
    },
    [selectResult, goTo, searchData?.results],
  )

  const nextSearchResult = useCallback(() => {
    if (searchStateValue?.resultIndex == null) {
      return
    }

    selectSearchResult(searchStateValue.resultIndex + 1)
  }, [selectSearchResult, searchStateValue])

  const previousSearchResult = useCallback(() => {
    if (searchStateValue?.resultIndex == null) {
      return
    }

    selectSearchResult(searchStateValue.resultIndex - 1)
  }, [selectSearchResult, searchStateValue])

  const endSearch = useCallback(() => {
    setUrlState({ searchQuery: null, at: null })
  }, [setUrlState])

  // Message selection for copy/quote functionality
  const messageSelection = useMessageSelection(communications ?? [], me?.id ?? 0)
  const { selectedCount, getSelectedMessagesForClipboard } = messageSelection

  const [isCopyFlash, setIsCopyFlash] = useState(false)
  const copyFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup copy flash timeout on unmount
  useEffect(() => {
    return () => {
      if (copyFlashTimeoutRef.current) {
        clearTimeout(copyFlashTimeoutRef.current)
      }
    }
  }, [])

  const handleCopy = useCallback(async () => {
    if (!person || selectedCount === 0) {
      return
    }
    const messages = getSelectedMessagesForClipboard()
    await copyMessagesToClipboard(person.id, messages)
    // Flash selected messages green to indicate successful copy
    setIsCopyFlash(true)
    // Clear any existing timeout before setting a new one
    if (copyFlashTimeoutRef.current) {
      clearTimeout(copyFlashTimeoutRef.current)
    }
    copyFlashTimeoutRef.current = setTimeout(() => setIsCopyFlash(false), 2000)
  }, [person, selectedCount, getSelectedMessagesForClipboard])

  const hasInitialLoadOccurred =
    communications !== undefined &&
    me !== undefined &&
    person !== undefined &&
    thread !== undefined &&
    case_ !== undefined
  if (!hasInitialLoadOccurred) {
    return <Loader />
  }

  return (
    <ChatContext.Provider
      value={{
        communications,
        setOptimisticallySentCommunication,
        me,
        person,
        thread,
        case: case_,
        at,
        findIndex,
        hasEarlierResults,
        fetchEarlierResults,
        isFetchingEarlierResults,
        hasLaterResults,
        fetchLaterResults,
        isFetchingLaterResults,
        goTo,
        search: {
          state: searchState.state,
          result: search,
          start: startSearch,
          query: searchQuery,
          setQuery: setSearchQuery,
          selectResult: selectSearchResult,
          nextResult: nextSearchResult,
          previousResult: previousSearchResult,
          backToResults: searchState.backToResults,
          end: endSearch,
        },
        selection: {
          ...messageSelection,
          handleCopy,
          isCopyFlash,
        },
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

/**
 * Hook to access chat context including message selection state.
 * Must be used within ChatLoader (i.e., within CommunicationThread).
 *
 * Provides:
 * - communications, me, person, thread, case, at - core chat data
 * - search - search state and actions
 * - selection - message selection state for copy/quote functionality
 */
export const useChat = () => {
  const value = useContext(ChatContext)
  if (!value) {
    throw new Error('useChat must be used within ChatLoader')
  }
  return value
}

interface CommunicationThreadProps {
  personId: BffCrmPersonChatRetrievePathParams['person_id']
}

const CommunicationThread: React.FC<CommunicationThreadProps> = props => {
  const { personId } = props

  return (
    <ChatLoader personId={personId}>
      <CommunicationInner />
    </ChatLoader>
  )
}

/**
 * Published by `publish_thread_updated` in api/ when a thread changes with no message to
 * announce it. It rides a PubNub signal, not a message, so it never enters channel
 * history and never reaches the `message:` listener below. It carries no detail, because
 * the channel names the thread.
 */
const THREAD_UPDATED_SIGNAL_TYPE = 'thread_updated'

const CommunicationInner = () => {
  const {
    me,
    person,
    thread,
    case: case_,
    search,
    selection,
    setOptimisticallySentCommunication,
    hasLaterResults,
    goTo,
  } = useChat()
  // Same reason as the outer component: depend on the callbacks, not the context object,
  // which is rebuilt on every render.
  const {
    selectedCount: selectedMessageCount,
    handleCopy: copySelectedMessages,
    clearSelection,
  } = selection
  const queryClient = useQueryClient()
  const [urlState] = useChatURLState()

  // Keyboard shortcut for copy: Cmd/Ctrl+C
  // Note: Escape to clear selection is handled in useMessageSelection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedMessageCount === 0) return

      const isCopy = (e.metaKey || e.ctrlKey) && e.key === 'c'
      if (isCopy) {
        e.preventDefault()
        void copySelectedMessages()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [selectedMessageCount, copySelectedMessages])

  const sendChatMutation = useSendChatMutation({
    // A user ID is required to send a message, but the bffCrmPersonChat endpoint can return
    // a person without a user. In that case, we don't allow sending messages.
    // This is locked in with an invariant in the hook.
    patient: { id: person.user?.id ?? null, person: { id: person.id } },
    onMessagePosted: data => {
      // Optimistically prepend a stub communication to all matching chat queries.
      const optimisticCommunication: BffCrmPersonChatRetrieveResponse['communications']['results'][number] =
        {
          content_object: {
            id: -Date.now(), // temporary negative ID; replaced by the real ID after refetch
            kind: 'chat',
            attachment: null,
            case_relations: [],
            event: null,
            file_attachment: null,
            is_urgent_message: null,
            message_tags: [],
            sender: {
              id: me.id,
              avatar_url: null,
              full_name_used: '',
              is_patient: false,
              role: '',
              title: '',
            },
            sent_at: new Date().toISOString(),
            text: data.text ?? '',
            thread: { case: null },
            uid: data.uid ?? '',
            relations: [],
          },
        }

      setOptimisticallySentCommunication(optimisticCommunication)
    },
    onSuccess: async () => {
      if (hasLaterResults) {
        // Note: This will effectively reset the cache.
        goTo(null)
      } else {
        await queryClient.invalidateQueries({ queryKey: ['chat', person.id] })
      }
      setOptimisticallySentCommunication(null)
    },
    onError: async () => {
      if (hasLaterResults) {
        // Note: This will effectively reset the cache.
        goTo(null)
      } else {
        await queryClient.invalidateQueries({ queryKey: ['chat', person.id] })
      }
      setOptimisticallySentCommunication(null)
    },
  })

  // Clear message selection when changing cases
  // This doesn't affect the clipboard or the "Quote" message action workflow
  useEffect(() => {
    clearSelection()
  }, [urlState.caseId, clearSelection])

  // Listen for new messages delivered via PubNub.
  const pubNub = useSelector(state => state.chat.pubnub)

  useEffect(() => {
    if (!pubNub) return
    if (!person.user) return

    const threadChannelPrefix = `thread.${person.user.id}`
    const channels = [threadChannelPrefix, `${threadChannelPrefix}.*`]

    const listener = {
      message: (e: PubNub.Subscription.Message) => {
        // The PubNub instance is already subscribed to "provider-message-inbox-{tenantId}"
        // for chat messages, so filter for phone_call events.
        if (e.channel.includes('provider-message-inbox')) {
          const msg = e.message as { type?: string; person_id?: number; handled_by_id?: number }
          // Handle phone_call_unlinked event to refresh the phone calls list
          // This enables the phone call icon to glow blue when a new unlinked call arrives
          // Only invalidate if the current user is the handler for this call
          if (msg.type === 'phone_call_unlinked' && msg.handled_by_id === me.id) {
            void queryClient.invalidateQueries({ queryKey: [PhoneCallsKey] })
            return
          }
          // Handle phone_call (linked) events that match the current person
          if (msg.type !== 'phone_call' || msg.person_id !== person.id) {
            return
          }
          // Also refresh the unlinked-calls cache that backs the Link Calls modal,
          // so a call linked by another actor disappears from the list.
          void queryClient.invalidateQueries({ queryKey: [PhoneCallsKey] })
        }
        // Invalidate to pull the canonical data from the server.
        void queryClient.invalidateQueries({ queryKey: ['chat', person.id] })
      },
      signal: (e: PubNub.Subscription.Signal) => {
        // The type test is the whole filter: everything else this client can deliver
        // fails it (the inbox presence signals carry no `type`, the typing signal is a
        // different one, and only this person's thread channels are subscribed).
        const signal = e.message as { type?: string } | null
        if (signal?.type !== THREAD_UPDATED_SIGNAL_TYPE) return
        void queryClient.invalidateQueries({ queryKey: ['chat', person.id] })
      },
    }

    pubNub.addListener(listener)
    pubNub.subscribe({ channels })

    return () => {
      pubNub.removeListener(listener)
      pubNub.unsubscribe({ channels })
    }
  }, [pubNub, person.user, person.id, me.id, queryClient])

  // Search handlers

  const searchInputRef = useRef<HTMLInputElement>(null)
  // Thread search mounts when the user opens it, so focus follows that action.
  useFocusOnOpen(searchInputRef, search.state != null)

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    const query = (e.target as HTMLInputElement).value
    if (query) {
      search.setQuery(query)
    }
  }

  const handleClearSearch = () => {
    // Start a new search.
    if (searchInputRef.current) {
      searchInputRef.current.value = ''
    }
    search.start()
  }

  // When the search query changes (e.g. from the URL), update the input value
  // to match.
  useEffect(() => {
    if (search.query === null || !searchInputRef.current) {
      return
    }

    searchInputRef.current.value = search.query
  }, [search.query])

  // Person Summary handlers

  const [summaryAnchorEl, setSummaryAnchorEl] = useState<HTMLElement | null>(null)

  const handleSummaryClick = (event: React.MouseEvent<HTMLElement>) => {
    setSummaryAnchorEl(event.currentTarget)
  }

  const handleSummaryClose = () => {
    setSummaryAnchorEl(null)
  }

  const summaryOpen = Boolean(summaryAnchorEl)

  // If null, we don't have enough information (e.g., missing insurance), and
  // choose to fail open.
  const canProvideCare = me.providerdetail.can_provide_care_to_person ?? true

  return (
    <>
      <Box
        sx={[
          borderSx(['borderBottom']),
          { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
        ]}
      >
        <Box sx={{ display: 'flex', flex: 1 }}>
          {case_ ? (
            <CaseMessageSearch case={case_} />
          ) : search.state ? (
            <>
              <IconButton
                sx={TOOLBAR_ICON_BUTTON_SX}
                style={{
                  backgroundColor: 'transparent',
                  cursor: 'default',
                }}
              >
                <Search />
              </IconButton>
              <InputBase
                inputRef={searchInputRef}
                placeholder="Search thread"
                inputProps={{ 'aria-label': 'search thread' }}
                onKeyDown={handleSearch}
                fullWidth
              />

              {search.state && search.result.data ? (
                <>
                  {search.state.view === 'single' ? (
                    <Button sx={TOOLBAR_ICON_BUTTON_SX} onClick={search.backToResults}>
                      Results
                    </Button>
                  ) : null}
                  {search.state.view === 'results' ? (
                    <Button sx={TOOLBAR_ICON_BUTTON_SX} onClick={handleClearSearch}>
                      Clear
                    </Button>
                  ) : null}
                  <Box sx={DIVIDER_SX}> </Box>
                  {search.state.resultIndex !== null ? (
                    <Box
                      sx={{
                        justifyContent: 'center',
                        display: 'flex',
                        textTransform: 'none',
                        color: 'grey.500',
                      }}
                    >
                      <IconButton
                        sx={ARROW_BUTTON_SX}
                        disabled={search.state.resultIndex <= 0}
                        onClick={search.previousResult}
                      >
                        <ChevronLeft />
                      </IconButton>
                      <IconButton
                        sx={ARROW_BUTTON_SX}
                        disabled={search.state.resultIndex + 1 >= search.result.data.results.length}
                        onClick={search.nextResult}
                      >
                        <ChevronRight />
                      </IconButton>
                    </Box>
                  ) : null}
                </>
              ) : null}
              <Box sx={DIVIDER_SX}> </Box>

              <IconButton sx={TOOLBAR_ICON_BUTTON_SX} onClick={() => search.end()}>
                <Close />
              </IconButton>
            </>
          ) : (
            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between' }}>
              {/* Search is only supported for chat messages, which is only available to Users */}
              {person.user ? (
                <Box sx={{ display: 'flex' }}>
                  <IconButton sx={TOOLBAR_ICON_BUTTON_SX} onClick={search.start}>
                    <Search />
                  </IconButton>
                </Box>
              ) : null}

              {/* Broken for Persons without Users: they fall back to id 0. */}
              <CurrentProfileViewers patientId={person.user?.id ?? 0} />
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex' }}>
          <AddPhoneCallButton person={person} />
          <IconButton sx={TOOLBAR_ICON_BUTTON_SX} onClick={handleSummaryClick}>
            <AutoAwesome sx={{ fill: summaryAnchorEl ? BLUE['main'] : 'primary.main' }} />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ flex: '1', position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            top: 0,
            left: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {search.state?.view === 'results' ? (
            <Box sx={{ overflow: 'auto' }}>
              <ChatSearchResultsV2
                searchQuery={search.result.currentQuery}
                searchResults={search.result.data ?? null}
                isLoading={search.result.isFetching}
                error={search.result.error}
                handleResultClick={id => {
                  const index = search.result.data?.results.findIndex(result => result.id === id)
                  if (index === undefined || index === -1) {
                    // If the selected search result ID is not actually in the
                    // results, something is wrong.
                    throw new Error('Search result not found')
                  }
                  search.selectResult(index)
                }}
              />
            </Box>
          ) : (
            <>
              <VirtualizedChat />
              {!canProvideCare ? (
                <Box
                  sx={theme => ({
                    display: 'flex' as const,
                    padding: '1rem',
                    backgroundColor: theme.palette.primary.light,
                    marginTop: '0.5rem',
                  })}
                >
                  <ReportProblemOutlined
                    sx={theme => ({ color: theme.palette.tones.warning.fg, marginRight: '1rem' })}
                  />
                  <Typography variant="body2">
                    {/* Note that the state should never really be unknown, since it
                must be known to determine that care cannot be provided. */}
                    {`This member is located in ${
                      person.insurance_info?.state ?? 'an unknown state'
                    }. You
              are unable to provide clinical care.`}
                  </Typography>
                </Box>
              ) : null}
              {thread?.is_locked ? (
                <>
                  <Divider />
                  <Box
                    sx={{
                      color: ({ palette }) => palette.grey['500'],
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      p: 2,
                    }}
                  >
                    <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                      <Lock color="inherit" />
                      <Typography variant="subtitle2" sx={{ ml: 1 }} component="span">
                        Conversation locked
                      </Typography>
                    </Typography>
                  </Box>
                </>
              ) : null}
              {person.user ? (
                <MacrosProvider>
                  <ChatInput
                    sendChat={sendChatMutation.mutateAsync}
                    sending={sendChatMutation.isPending}
                    patient={{
                      id: person.user.id,
                      first_name: person.first_name_used,
                      person: {
                        id: person.id,
                        preferred_name: person.first_name_used,
                      },
                    }}
                    showIntroductionReminder={!me.providerdetail.has_chatted_with_person}
                    providerCanProvideClinicalCareToPatient={canProvideCare}
                    threadUid={thread?.uid ?? null}
                    isLocked={thread?.is_locked ?? false}
                  />
                  <Box sx={{ position: 'absolute', bottom: 0, left: 2 }}>
                    <ChatTypingIndicator patientId={person.user.id} currentUser={me} />
                  </Box>
                </MacrosProvider>
              ) : null}
            </>
          )}
        </Box>
      </Box>

      {/* Person Summary Popover */}
      <Popover
        open={summaryOpen}
        anchorEl={summaryAnchorEl}
        onClose={handleSummaryClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: '28.5em',
              maxWidth: '28.5em',
            },
          },
        }}
      >
        <PersonChatSummaryCard personId={person.id} onClose={handleSummaryClose} />
      </Popover>
    </>
  )
}

type ChatCommunication = BffCrmPersonChatRetrieveResponse['communications']['results'][number]

const isSameMessage = (first?: ChatCommunication, second?: ChatCommunication) =>
  first?.content_object.kind === second?.content_object.kind &&
  first?.content_object.id === second?.content_object.id

// A new first message means history was prepended; a new last message means one was appended.
const nextScrollShift = (
  previous: ChatCommunication[],
  current: ChatCommunication[],
  shift: boolean,
) => {
  if (previous.length === 0 || current.length === 0) return false
  if (!isSameMessage(previous.at(0), current.at(0))) return true
  if (!isSameMessage(previous.at(-1), current.at(-1))) return false
  return shift
}

const VirtualizedChat = () => {
  const {
    communications,
    at,
    findIndex,
    hasEarlierResults,
    fetchEarlierResults,
    isFetchingEarlierResults,
    hasLaterResults,
    fetchLaterResults,
    isFetchingLaterResults,
    goTo,
  } = useChat()
  const ref = useRef<VirtualizerHandle>(null)
  // The `shift` parameter determines from which direction scroll position is
  // maintained.
  // - When false, the scroll position is maintained from the top, which is
  //   appropriate when appending to the end of the list.
  // - When true, the scroll position is maintained from the bottom, which is
  //   appropriate when prepending to the beginning of the list.
  const [shift, setShift] = useState(false)
  const [previousCommunications, setPreviousCommunications] = useState(communications)
  if (communications !== previousCommunications) {
    setPreviousCommunications(communications)
    setShift(nextScrollShift(previousCommunications, communications, shift))
  }

  // Scroll to index if specified. Use 'bottom' as shorthand to scroll to the
  // bottom.
  const shouldScrollToIndexRef = useRef<number | 'bottom' | null>(
    at ? (findIndex() ?? null) : 'bottom',
  )

  // Scrolling effects.
  useLayoutEffect(() => {
    if (!ref.current) {
      return
    }

    if (shouldScrollToIndexRef.current === null) {
      return
    }

    if (shouldScrollToIndexRef.current === 'bottom') {
      // HACK: `scrollToIndex` refers to the index of the items rendered by the
      // virtualizer. The virtualizer renders more than just communications (it
      // also renders loading indicators, read receipts, etc.). So simply
      // scrolling to `communications.length - 1` is incorrect.
      //
      // Since virtua clamps the index to the length of items in its cache,
      // setting the index to `Infinity` effectively scrolls to the bottom.
      //
      // See https://github.com/inokawa/virtua/blob/fe82ad4c2a57effc55a9d40f156f59c6cf7b894b/src/core/scroller.ts#L378
      ref.current.scrollToIndex(Infinity, { align: 'end' })
    } else {
      const index = shouldScrollToIndexRef.current
      if (index < 0) {
        // Index should have been found if API is working correctly...
        return
      }
      // As noted above, `scrollToIndex` is not referring to the index in
      // `communications` but the entire list of items rendered by the
      // virtualizer. Right now it is at most off by one due to the "no more
      // messages" or the loading indicator, so the offset is not noticeable.
      ref.current.scrollToIndex(index, { align: 'center' })
    }
  })

  return (
    <Box style={{ overflowY: 'auto' }} sx={{ flex: 1 }}>
      <Box
        // Spacer to keep container from shrinking even when all contents fit
        // inside viewport.
        sx={{ flexGrow: 1 }}
      />
      <Virtualizer
        ref={ref}
        shift={shift}
        onScroll={offset => {
          if (!ref.current) {
            return
          }

          // Round to account for sub-pixels.
          const scrollSize = Math.round(ref.current.scrollSize)
          const viewportSize = Math.round(ref.current.viewportSize)
          const top = Math.round(offset)
          const bottom = top + viewportSize

          // In general, cancel any auto-scrolling once user starts manually
          // scrolling.
          //
          // Exception: If scrolled to the bottom, stick to the bottom. This
          // allows new messages to appear without the user having to scroll
          // them into view.
          if (!hasLaterResults && bottom >= scrollSize) {
            shouldScrollToIndexRef.current = 'bottom'
          } else {
            shouldScrollToIndexRef.current = null
          }

          // Determine if we're close enough (in terms of multiples of the
          // viewport) to the top or bottom to fetch more results.
          const thresholdSize = viewportSize * 0.5 // Half of viewport - adjust to taste.
          const topThreshold = thresholdSize
          const bottomThreshold = scrollSize - thresholdSize
          if (top <= topThreshold && hasEarlierResults && !isFetchingEarlierResults) {
            void fetchEarlierResults()
          } else if (bottom >= bottomThreshold && hasLaterResults && !isFetchingLaterResults) {
            void fetchLaterResults()
          }
        }}
      >
        {!hasEarlierResults ? (
          !hasLaterResults && communications.length === 0 ? (
            <Box sx={{ textAlign: 'center', fontSize: '1.2rem' }}>No messages yet</Box>
          ) : (
            <Box sx={{ textAlign: 'center', fontSize: '1.2rem' }}>No more messages</Box>
          )
        ) : null}
        {isFetchingEarlierResults ? <InlineLoader /> : null}
        {/* Note: I'd like to factor this out into a sub-component like
        `<Communications />`, but virtua appears to need its immediate children
        to be the items to be virtualized. */}
        {communications.map((communication, i) => {
          const { content_object } = communication

          const isLast = !hasLaterResults && i === communications.length - 1

          return (
            <Box key={`${content_object.kind}-${content_object.id}`} sx={{ px: 1 }}>
              <Communication communication={communication} isLast={isLast} />
            </Box>
          )
        })}
        {isFetchingLaterResults ? <InlineLoader /> : null}
      </Virtualizer>
      {/* Show "Back to Recent" accelerator if there are later pages to load. */}
      {hasLaterResults ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', position: 'sticky', bottom: '1rem' }}>
          <Button
            endIcon={<ArrowDownward />}
            variant="contained"
            size="small"
            onClick={() => {
              goTo(null)
            }}
          >
            Back to Recent
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}

const InlineLoader = () => (
  <Box
    sx={theme => ({
      position: 'relative',
      height: '7.5rem',
      color: theme.palette.primary.light,
    })}
  >
    <Loader inline />
  </Box>
)

const Communication: React.FC<{
  communication: BffCrmPersonChatRetrieveResponse['communications']['results'][number]
  isLast: boolean
}> = ({ communication, isLast }) => {
  const { me, person, thread, case: case_, at } = useChat()
  const { content_object } = communication

  const isSelected = at?.kind === content_object.kind && at.objectId === content_object.id
  const isLinkedToSelectedCase = Boolean(
    case_ &&
    // This check may be redundant since the API, given a case filter,
    // should never return messages unrelated to the case.
    content_object.case_relations.some(
      (relation: { case: { id: number } }) => relation.case.id === case_.id,
    ),
  )

  if (content_object.kind === 'chat') {
    // Any event value, not just the ones this build knows about. An event row carries
    // no text of its own — the BFF supplies its copy — so falling through to the
    // message branch would draw an empty bubble complete with avatar and timestamp.
    if (content_object.event) {
      return (
        <ChatMessageEvent
          message={content_object}
          person={person}
          threadUid={thread?.uid ?? null}
        />
      )
    }

    // Only Users can send and receive chat messages. If the API returns a chat
    // message for a Person without a User, something has gone very wrong.
    assertPropertyIsNotNull(person, 'user')

    const mine = content_object.sender.id === me.id
    // Optimistic messages use a temporary negative ID and have no real sender info yet.
    const isOptimistic = content_object.id < 0

    // Only messages in the legacy thread can be linked to and unlinked from
    // cases.
    const isInLegacyThread = content_object.thread.case === null
    const canCase = isInLegacyThread
    // To unlink a message from a case, we must also be viewing the message's
    // legacy thread filtered down to a single case one of the message's cases.
    const isViewingLegacyThread = thread?.case === null
    const canUncase = Boolean(
      isInLegacyThread &&
      isViewingLegacyThread &&
      case_ &&
      // This check is a bit redundant since the API, given a case filter,
      // should never return messages unrelated to the case.
      content_object.case_relations.some(
        (relation: { case: { id: number } }) => relation.case.id === case_.id,
      ),
    )

    const personLastReadAt =
      thread?.members.find(
        member =>
          member.user.id === person.user.id &&
          member.last_read_chat_message_id === content_object.id,
      )?.last_read_at ?? null

    return (
      <ChatMessage
        message={content_object}
        mine={mine}
        person={person}
        patientReadAt={personLastReadAt}
        highlightChatMessageContent={isSelected || isLinkedToSelectedCase || isOptimistic}
        isLastMessage={isLast}
        threadUid={thread?.uid ?? null}
        canCase={canCase}
        canUncase={canUncase}
        isOptimistic={isOptimistic}
      >
        <ChatMessageRelations relations={content_object.relations} personId={person.id} />
        {content_object.file_attachment ? (
          <ChatMessageDocument
            user={content_object.sender}
            id={content_object.file_attachment.id}
          />
        ) : null}
        {content_object.attachment ? (
          <ChatMessageAttachment
            userId={content_object.sender.id}
            attachment={content_object.attachment.id}
            mine={mine}
          />
        ) : null}
      </ChatMessage>
    )
  } else if (content_object.kind === 'email') {
    return (
      <EmailChatMessage
        email={content_object}
        key={`email-${content_object.id}`}
        highlightChatMessageContent={isLinkedToSelectedCase}
      />
    )
  } else if (content_object.kind === 'phone') {
    return (
      <PhoneCallMessage
        phoneCall={content_object}
        person={person}
        highlightChatMessageContent={isLinkedToSelectedCase}
        isLastMessage={isLast}
        threadUid={thread?.uid ?? null}
      />
    )
  }
  assertNever(content_object)
}

export default CommunicationThread
